import { useState, useEffect } from 'react';
import { comprasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { RefreshCw, DollarSign, Printer, TrendingUp, FileSpreadsheet } from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

export const CostoMaterialesOT = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await comprasApi.getCostoMaterialesPorOT();
      setData(res.data);
    } catch (err) {
      setError('Error al cargar los datos');

    } finally {
      setLoading(false);
    }
  };

  const fmt = (num) => num?.toLocaleString('es-CL') || '0';

  const handlePrint = () => {
    if (!data?.costos_por_ot?.length) return;
    printTable({
      title: 'Costo de Materiales por OT',
      subtitle: `${data.cantidad_ots} OTs · Valores netos · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'OTs con costos', value: data.cantidad_ots },
        { label: 'Total general (neto)', value: `$${fmt(data.total_general_neto)}`, color: '#047857', highlight: true },
      ],
      columns: [
        { key: 'ot_numero', label: 'OT', mono: true, align: 'center', width: '60px' },
        { key: 'descripcion_ot', label: 'Descripción' },
        { key: 'cantidad_oc', label: 'Cant. OC', align: 'center' },
        { key: 'costo_materiales_neto', label: 'Costo Neto', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
      ],
      rows: data.costos_por_ot,
      totals: [{ label: 'TOTAL GENERAL', value: `$${fmt(data.total_general_neto)}`, span: 3 }],
    });
  };

  const handleExcel = () => {
    if (!data?.costos_por_ot?.length) return;
    exportToCSV({
      filename: 'costo_materiales_por_ot',
      title: 'CHIRINO STEEL · Costo de Materiales por OT (Netos)',
      summary: [
        { label: 'OTs con costos', value: data.cantidad_ots },
        { label: 'Total general (CLP)', value: Math.round(data.total_general_neto || 0) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'ot_numero', label: 'OT' },
        { key: 'descripcion_ot', label: 'Descripción' },
        { key: 'cantidad_oc', label: 'Cantidad OC', format: (v) => Number(v) || 0 },
        { key: 'costo_materiales_neto', label: 'Costo Neto (CLP)', format: (v) => Math.round(Number(v) || 0) },
      ],
      rows: data.costos_por_ot,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error}</p>
        <Button onClick={loadData} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="costo-materiales-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Costo de Materiales por OT
          </h1>
          <p className="text-sm text-muted-foreground">
            Basado en Órdenes de Compra aprobadas - <span className="text-green-600 font-medium">Valores Netos</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm" data-testid="btn-print-costo-ot">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button onClick={handleExcel} variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-costo-ot">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-4 border-b-2 border-black pb-2">
        <h1 className="text-lg font-bold">COSTO DE MATERIALES POR OT (Valores Netos)</h1>
        <p className="text-sm">Basado en OC Aprobadas | {data?.cantidad_ots} OTs</p>
      </div>

      {/* Summary Card */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-green-700">Total General (Neto)</p>
            <p className="text-3xl font-bold text-green-700">${fmt(data?.total_general_neto)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">{data?.cantidad_ots} OTs con costos</p>
          </div>
        </div>
      </div>

      {/* Table */}
      {data?.costos_por_ot?.length > 0 ? (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-800 text-white">
              <th className="border border-gray-600 p-2 text-left w-20">OT</th>
              <th className="border border-gray-600 p-2 text-left">Descripción</th>
              <th className="border border-gray-600 p-2 text-center w-24">Cant. OC</th>
              <th className="border border-gray-600 p-2 text-right w-32">Costo Neto</th>
            </tr>
          </thead>
          <tbody>
            {data.costos_por_ot.map((item, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border p-2 font-mono font-medium">{item.ot_numero}</td>
                <td className="border p-2 truncate max-w-[300px]">{item.descripcion_ot || '-'}</td>
                <td className="border p-2 text-center">{item.cantidad_oc}</td>
                <td className="border p-2 text-right font-mono text-green-600 font-medium">
                  ${fmt(item.costo_materiales_neto)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-800 text-white font-bold">
              <td colSpan={3} className="border border-gray-600 p-2 text-right">TOTAL GENERAL:</td>
              <td className="border border-gray-600 p-2 text-right font-mono">${fmt(data?.total_general_neto)}</td>
            </tr>
          </tfoot>
        </table>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <TrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-muted-foreground">No hay datos de costos de materiales</p>
          <p className="text-sm text-muted-foreground mt-1">Importe las órdenes de compra desde Access</p>
        </div>
      )}
    </div>
  );
};
